import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ClassNameProps, DisabledProps, InputRefProps, Nullable } from '@/Types';
import { useFormError, useFormSetValue, useLocalization } from '@/Hooks';
import jQuery from 'jquery';
import daterangepicker from 'daterangepicker';
import 'daterangepicker/daterangepicker.css';
import cn from 'classnames';
import { ErrorTooltip } from '@/Components/Controls/ErrorTooltip/ErrorTooltip';
import { useDaterangeDefaultOptions } from './hooks/useDaterangeDefaultOptions';
import { useDaterangePickerLocale } from './hooks/useDaterangePickerLocale';
import { useDaterangePickerRanges } from './hooks/useDaterangePickerRanges';
import { useDateRangeEventListeners } from './hooks/useDaterangeEventListeners';
import { v4 as uuidv4 } from 'uuid';
import { formDateInputUtils } from '@/Components/Controls/DateInput/utils';
import styles from './FormDateInput.scss';
import { InputMask } from 'imask';
import { CalendarIcon } from './modules/CalendarIcon/CalendarIcon';
import moment from 'moment-timezone';

type Props<TFormData extends object> = {
  showCalendar?: boolean;
  showCustomRangeLabel?: boolean;
  singleDatePicker?: boolean;
  valueForSet?: [string, string] | string;
  onBlur?: () => void;
  onChange?: (val: string) => void;
  minDate?: string | Date;
  maxDate?: string | Date;
  rangesType?: 'orderDateRanges' | 'defaultDateRanges';
  needTimePicker?: boolean;
  tabIndex?: number;
} & ClassNameProps &
  DisabledProps &
  Omit<InputRefProps<HTMLInputElement, TFormData, string | number>, 'valueForSet'>;

export function FormDateInput<TFormData extends object>({
  name,
  setValue,
  onFocus,
  onBlur,
  onChange,
  error,
  className,
  showCalendar = true,
  showCustomRangeLabel = false,
  singleDatePicker = true,
  disabled = false,
  register,
  rules,
  valueForSet = '',
  minDate,
  maxDate,
  needTimePicker = false,
  rangesType,
  tabIndex,
}: Props<TFormData>) {
  const { validation } = useLocalization();

  // Options
  const needInputMask = singleDatePicker;
  const endOfToday = moment.tz(moment.tz.guess()).endOf('day').toDate();
  const defaultMaxDate = maxDate ?? endOfToday;
  const maxAllowedDate = !rangesType ? defaultMaxDate : undefined;

  // Form
  useFormSetValue(name, valueForSet, setValue);

  const [displayValue, setDisplayValue] = useState('');
  const errorMessage = useFormError(name, error);
  const ranges = useDaterangePickerRanges(rangesType);
  const locale = useDaterangePickerLocale(needTimePicker, singleDatePicker);

  const inputId = useMemo(() => uuidv4(), []);
  const maskRef = useRef<Nullable<InputMask>>(null);
  const inputRef = register?.(name, {
    validate: (value) => {
      if (!value || !needInputMask) return true;

      const validateRegex = formDateInputUtils.createValidationRegex(locale.format);
      return validateRegex.test(String(value).trim()) || validation.DateIsInvalid;
    },
    ...rules,
  });

  const defaultOptions = useDaterangeDefaultOptions({
    singleDatePicker,
    needTimePicker,
    showCustomRangeLabel,
    showCalendar,
    valueForSet,
    minDate,
    maxDate: maxAllowedDate,
    format: locale.format,
  });

  // Daterange options
  const options: daterangepicker.Options = {
    ...defaultOptions,
    locale: locale,
    ranges,
  };

  // Event listeners
  const { onApply, onCancel, onSetValue, onSetCurrentDate } = useDateRangeEventListeners<TFormData>({
    name,
    setValue,
    maskRef,
    maxDate: maxAllowedDate,
    format: locale.format,
    isRange: !singleDatePicker,
    onChange,
  });

  useEffect(() => {
    setDisplayValue(formDateInputUtils.convertDateOrRangeToString(valueForSet, ranges, locale.format));
  }, [valueForSet]);

  useEffect(() => {
    const value = String(jQuery(`#${inputId}`).val());
    setDisplayValue(formDateInputUtils.convertDateOrRangeToString(value, ranges, locale.format));
  }, [inputRef]);

  useEffect(() => {
    // Mask initializing
    const input = jQuery(`#${inputId}`).get(0);

    if (!disabled && needInputMask && input) {
      maskRef.current = formDateInputUtils.createDateMask(input, {
        dateFormat: locale.format,
        maxDate: maxAllowedDate,
        minDate,
        onSetValue,
      });

      return () => {
        maskRef?.current?.destroy();
      };
    }
  }, [inputId, locale.format]);

  useEffect(() => {
    // Daterange initializing
    const input = jQuery(`#${inputId}`);

    if (input) {
      const dateRange = new daterangepicker(input.get(0), options);

      // Daterange event handlers
      input.on('apply.daterangepicker', (event, picker) => {
        onApply(picker, setDisplayValue);
      });

      input.on('cancel.daterangepicker', () => {
        onCancel(setDisplayValue);
      });

      input.on('hide.daterangepicker', () => {
        onBlur?.();
      });

      return () => {
        dateRange?.remove();
      };
    } // if
  }, [options, inputId]);

  return (
    <ErrorTooltip error={errorMessage} className={cn(className, styles.inputWrapper)}>
      <input
        type="text"
        id={`${inputId}`}
        {...inputRef}
        name={name as string}
        disabled={disabled}
        className={cn(styles.daterange, {
          [styles.daterange__hidden]: !needInputMask,
          [styles.error]: errorMessage,
        })}
        autoComplete={'off'}
        tabIndex={tabIndex}
        placeholder={disabled ? '' : locale.format}
        onKeyDown={onSetCurrentDate}
      />
      <input
        type="text"
        title={displayValue}
        id={`${inputId}_visible`}
        disabled={disabled}
        className={cn(styles.daterange, styles.daterange__disabled, {
          [styles.daterange__hidden]: needInputMask,
          [styles.error]: errorMessage,
        })}
        autoComplete={'off'}
        readOnly={false}
        onClick={() => {
          jQuery(`#${inputId}`).trigger('click');
        }}
        onFocus={onFocus}
        value={displayValue}
        tabIndex={tabIndex}
      />

      <CalendarIcon />
    </ErrorTooltip>
  );
}
